import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Box,
   Flex,
   Heading,
   ScrollArea,
   Text,
   Table,
   Button,
   Checkbox,
   Dialog,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { Paginator } from 'shared/components/pagination';
import { ArrowDownwardIcon, ArrowUpwardIcon } from 'shared/icons';

import { PlansActionsMenu } from './plans-actions-menu';
import { PlansMangerFilters } from './plans-manger-filters';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import APSidebar from 'Components/adminpanel/APSidebar/APSidebar';
import { TableLoaderOverlay } from 'shared/components';
import { usePlansManager } from './plans-manager.provider';

import './plans-manager.scss';
import { PlanActionConfirmDialog } from './plan-action-confirm-dialog';
import { toast } from 'sonner';

type ManagePlansAction = 'ACTIVATE_ALL' | 'DEACTIVATE_ALL' | 'EMPTY';

function PlansManagerView() {
   const navigate = useNavigate();
   const plansManager = usePlansManager();
   const [open, setOpen] = React.useState<boolean>(false);
   const [action, setAction] = React.useState<ManagePlansAction>('EMPTY');

   React.useEffect(() => {
      plansManager.loadPlans();
   }, []);

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="plans-manager">
            <Flex width="270px" minWidth="270px">
               <APSidebar />
            </Flex>
            <Box className="plans-manager__content">
               <Flex py="1" mx="3" justify="between">
                  <Heading as="h1" weight="medium">
                     Plans List
                  </Heading>
                  <Button size="3" onClick={() => navigate('/admin/plans/addplan')}>
                     Add Plan
                  </Button>
               </Flex>

               <PlansMangerFilters />

               <ScrollArea scrollbars="both" type="always" className="plans-manager__scroll-area">
                  <Box p="3">
                     <Table.Root size="3" layout="auto" variant="surface">
                        <Table.Header>
                           <Table.Row>
                              <Table.ColumnHeaderCell align="center">
                                 <Flex gap="1" justify="center">
                                    <Checkbox
                                       size="3"
                                       color="blue"
                                       checked={plansManager.selectedAll}
                                       onCheckedChange={(cheked) => {
                                          if (cheked) {
                                             plansManager.onSelectAll();
                                          } else {
                                             plansManager.onDeselectAll();
                                          }
                                       }}
                                       highContrast
                                    ></Checkbox>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">Plan Name</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          plansManager.onSortBy('plan_name');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[plansManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">User Plan</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          plansManager.onSortBy('planuser');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[plansManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">Amount</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          plansManager.onSortBy('amount');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[plansManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">Plan Type</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          plansManager.onSortBy('planuser');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[plansManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell>
                                 <Flex justify="center">
                                    <span className="header-label">Time Period</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          plansManager.onSortBy('type_value');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[plansManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell>
                                 <Flex gap="1" align="center">
                                    <span className="header-label">Created</span>
                                    <button
                                       className="sort-trigger"
                                       onClick={() => {
                                          plansManager.onSortBy('created');
                                       }}
                                    >
                                       {
                                          {
                                             asc: <ArrowDownwardIcon width={18} height={18} />,
                                             desc: <ArrowUpwardIcon width={18} height={18} />,
                                          }[plansManager.direction]
                                       }
                                    </button>
                                 </Flex>
                              </Table.ColumnHeaderCell>

                              <Table.ColumnHeaderCell justify="center">
                                 Action
                              </Table.ColumnHeaderCell>
                           </Table.Row>
                        </Table.Header>

                        <Table.Body>
                           <TableLoaderOverlay
                              show={plansManager.loadPlansState.status === 'pending'}
                              colSpan={7}
                           />

                           {plansManager.plans.map((plan) => {
                              return (
                                 <Table.Row key={plan.id}>
                                    <Table.Cell width="80px" justify="center">
                                       <Checkbox
                                          color="blue"
                                          size="2"
                                          highContrast
                                          checked={plansManager.idsArray.includes(plan.id)}
                                          onCheckedChange={() => {
                                             plansManager.onToggleById(plan.id);
                                          }}
                                       />
                                    </Table.Cell>

                                    <Table.Cell width="500px">{plan.plan_name}</Table.Cell>

                                    <Table.Cell width="220px">{plan.planuser}</Table.Cell>

                                    <Table.Cell>{plan.amount}</Table.Cell>

                                    <Table.Cell>{plan.type}</Table.Cell>

                                    <Table.Cell width="140px" align="center">
                                       {plan.type_value}
                                    </Table.Cell>

                                    <Table.Cell width="150px">{plan.created}</Table.Cell>

                                    <Table.Cell align="center" width="100px">
                                       <PlansActionsMenu plan={plan} />
                                    </Table.Cell>
                                 </Table.Row>
                              );
                           })}
                        </Table.Body>
                     </Table.Root>
                  </Box>
               </ScrollArea>

               <Flex className="slider-manager__pagination" py="2">
                  <Flex gap="4" px="3">
                     <Button
                        variant="surface"
                        size="3"
                        disabled={plansManager.diasableActivateAll}
                        onClick={() => {
                           setOpen(true);
                           setAction('ACTIVATE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           ACTIVATE
                        </Text>
                     </Button>

                     <Button
                        variant="outline"
                        size="3"
                        disabled={plansManager.diasableDeactivateAll}
                        onClick={() => {
                           setOpen(true);
                           setAction('DEACTIVATE_ALL');
                        }}
                     >
                        <Text weight="regular" size="2">
                           DEACTIVATE
                        </Text>
                     </Button>
                  </Flex>

                  <Paginator
                     totalPages={plansManager.totalPages}
                     currentPage={plansManager.pagination.page}
                     onPageClick={(p) => {
                        plansManager.pagination.setPage(p);
                     }}
                     setRange={(p) => {
                        plansManager.pagination.setRange(p);
                     }}
                     range={plansManager.pagination.range}
                  />
               </Flex>
            </Box>
         </Box>

         <Dialog.Root
            open={open}
            onOpenChange={(o) => {
               if (!o) {
                  setAction('EMPTY');
               }
               setOpen(o);
            }}
         >
            <Dialog.Content
               aria-describedby={undefined}
               onOpenAutoFocus={(e) => e.preventDefault()}
            >
               {
                  {
                     DEACTIVATE_ALL: (
                        <PlanActionConfirmDialog
                           title="Deactivate Plans?"
                           message="Do you want to Deactivate Plans?"
                           loading={plansManager.deactivateAllState.status === 'pending'}
                           onConfirm={() => {
                              plansManager.deactivateAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success('Plans Deactivated successfully!');
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     ACTIVATE_ALL: (
                        <PlanActionConfirmDialog
                           title="Activate Plans?"
                           message="Do you want to Activate Plans?"
                           loading={plansManager.activateAllState.status === 'pending'}
                           onConfirm={() => {
                              plansManager.activateAll({
                                 onSuccess: (resp) => {
                                    setOpen(false);
                                    toast.success('Plans Activated successfully!');
                                 },
                                 onError: (err) => {
                                    toast.error(err);
                                 },
                              });
                           }}
                        />
                     ),
                     EMPTY: <></>,
                  }[action]
               }
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
}

export const PlansManager = observer(PlansManagerView);
